import { PortalUser } from '@/common/components/ecorr-store/portal-user/portal-user';
import { ParadataEventEnum, ParadataEventService } from '@/common/paradata-event/paradata-event.service';
import { FileUtilService } from '@/common/utils/file-util.service';
import { AckDialogData } from '@/support/dialogs/ack-dialog/ack-dialog-data';
import { HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { AckDialogService } from '@/support/dialogs/services';
import { Component, HostListener, Inject, OnDestroy, OnInit, inject, signal, computed, DestroyRef } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription, lastValueFrom } from 'rxjs';
import { AddCommentDialogData } from './add-comment-dialog-data';
import { AddCommentDialogHttpService } from '../services';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { LinearLoadingComponent } from '@/common/components/linear-loading/linear-loading.component';
import { AckDialogComponent } from '@/support/dialogs/ack-dialog/ack-dialog.component';
import { CancelConfirmDialogComponent } from '@/support/dialogs/cancel-confirm-dialog/cancel-confirm-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-add-comment-dialog',
    templateUrl: './add-comment-dialog.component.html',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatDialogModule,
        MatInputModule,
        LinearLoadingComponent,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatIconModule,
        MatCardModule,
        AckDialogComponent,
        CancelConfirmDialogComponent
    ],
    providers: []
})
export class AddCommentDialogComponent implements OnInit, OnDestroy {
    // Services using inject()
    private ackDialogService = inject(AckDialogService);
    private addCommentDialogHttpService = inject(AddCommentDialogHttpService);
    private matDialogRef = inject(MatDialogRef<AddCommentDialogComponent>);
    private fileUtilService = inject(FileUtilService);
    private paradataEventService = inject(ParadataEventService);
    private destroyRef = inject(DestroyRef);

    // Signals for reactive state
    loading = signal(false);
    submitting = signal(false);
    submitCaseButtonDisabled = signal(false);
    commentAfterBlur = signal(false);
    files = signal<File[]>([]);
    submitClicked = signal(false);
    errorMessage = signal<string>('');
    commentHasContent = signal(false);

    // Computed signals
    isLoading = computed(() => this.loading());
    isSubmitting = computed(() => this.submitting());
    isSubmitDisabled = computed(() => this.submitCaseButtonDisabled());
    currentFiles = computed(() => this.files());
    hasError = computed(() => this.errorMessage().length > 0);
    canSubmit = computed(() => this.commentHasContent() && !this.isSubmitDisabled());

    // Other properties
    fullCaseID: string;
    user: PortalUser;
    comment: string;
    ack$: Subscription;
    
    /** File start */
    accept: any = {};
    maxSize: number = 1024 * 1024 * 50; /** 50 MB Maximum */
    lastInvalids: any;
    progress: number;
    lastFileAt: Date;
    httpEmitter: Subscription;
    uploadPercent: number = 70;

    formGroup = new FormGroup({
        comment: new FormControl('', [Validators.required])
    });

    constructor(
        @Inject(MAT_DIALOG_DATA) public addCommentDialogData: AddCommentDialogData
    ) {
    }

    async uploadFiles() {
        const formParam: any = {};
        formParam.comment = this.formGroup.get('comment').value;
        formParam.submittedByEmail = this.user.email;
        formParam.submittedByFirstName = this.user.firstName;
        formParam.submittedByLastName = this.user.lastName;

        const formData = new FormData();
        const currentFiles = this.files();
        if (currentFiles && currentFiles.length > 0) {
            for ( let i = 0; i < currentFiles.length; i++ ){
                formData.append('files[' + i + ']', currentFiles[i], currentFiles[i].name);
            }
        }

        this.loading.set(true);
        this.submitCaseButtonDisabled.set(true);
        this.errorMessage.set('');
        this.paradataEventService.add(ParadataEventEnum.Caseswithattachmentsfromcomments);
        await lastValueFrom(this.addCommentDialogHttpService.uploadFiles(formParam, formData, this.fullCaseID))
            .then(
                response => {
                    if (response.status === 200) {
                        this.confirm();
                    }
                },
                error => {
                    if (error.status === 422 && error.error && typeof error.error.message === 'string') {
                        this.errorMessage.set(error.error.message);
                    } else if (error.error && error.error.message && error.error.message.errors) {
                        this.errorMessage.set(error.error.message.errors[0].message);
                    } else {
                        this.errorMessage.set('Unknown server side error');
                    }
                }
            ).finally(
                () => {
                    this.loading.set(false);
                    this.submitCaseButtonDisabled.set(false);
                }
            );
    }

    ngOnInit() {
        this.loading.set(true);
        this.fullCaseID = 'USCB-ECORR-WORK ' + this.addCommentDialogData.caseId;
        this.user = this.addCommentDialogData.user;
    
        const commentControl = this.formGroup.get('comment');
        this.commentHasContent.set(!!commentControl.value?.trim().length);
    
        commentControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value: string) => {
                this.commentHasContent.set(!!value?.trim().length);
            });
    
        this.loading.set(false);
    }

    showError(field: string) {
        if (this.formGroup.get(field) && this.formGroup.get(field).invalid && !this.formGroup.get(field).pristine) {
            return true;
        }
    }

    getFileIcon(name: string) {
        return this.fileUtilService.getFileIcon(name);
    }

    fileChanged( $event ) {
        const newFiles = $event.target.files;
        if ( newFiles.length > 0 ) {
            const currentFiles = this.files();
            const updatedFiles = [...currentFiles];
            
            for ( let i = 0; i < newFiles.length; i++ ) {
                let dupe = false;
                for ( let j = 0; j < currentFiles.length; j++ ) {
                    if ( newFiles[i] === currentFiles[j] ) {
                        dupe = true;
                        break;
                    }
                }

                if ( !dupe && newFiles[i].size < this.maxSize ) {
                    updatedFiles.push( newFiles[i] );
                }
            }
            this.files.set(updatedFiles);
        }
    }

    removeFile( idx: number ) {
        const currentFiles = this.files();
        const updatedFiles = currentFiles.filter((_, index) => index !== idx);
        this.files.set(updatedFiles);
    }

    confirm() {
        this.close('confirm');
    }

    cancel() {
        this.close('cancel');
    }

    close(value: any) {
        this.matDialogRef.close(value);
    }

    onSubmit() {
        this.loading.set(true);

        const currentFiles = this.files();
        let dup = false;
        if (currentFiles.length > 1) {
            for (let i = 0; i < currentFiles.length && !dup; i++) {
                for ( let j = 0; j < currentFiles.length; j++ ) {
                    if ( currentFiles[j].name === currentFiles[i].name && i !== j) {
                        dup = true;
                    }
                }
            }
        }

        if (dup) {
            this.loading.set(false);
            this.openAckDialog();
        } else {
            this.uploadFiles();
        }
    }

    @HostListener('keydown.esc')
    public onEsc() {
        this.close('ESC keydown');
    }

    openAckDialog() {
        const options: AckDialogData = {
            title: 'Information',
            content: 'Please delete duplicated attachement file(s).',
            ackButtonLabel: 'OK'
        };
        this.ackDialogService.open(options);

        this.ack$ = this.ackDialogService.acked()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(res => { });
    }

    ngOnDestroy() {
        this.paradataEventService.flushParadataevents();
        if (this.ack$) {
            this.ack$.unsubscribe();
        }
    }
}
